import {customElement, property} from 'lit/decorators.js';
import {CSSResult, html, LitElement, PropertyValues, unsafeCSS,} from 'lit';
import {consume} from '@lit/context';
import trashIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_delete.svg?element';
import css from './wishlist-remove-button.scss?inline';
import {wishlistService} from '../../../scripts/service/wishlist-service.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {Messages, Product, WishlistCollection} from '../../../@types/wishlist.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {productsContext} from '../../../scripts/context/wishlistContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {ProductsState} from '../../../scripts/context/wishlist-state.js';
import {productMenuContext} from '../../../scripts/context/menuContext.js';
import {ProductMenuState} from '../../../scripts/context/menu-state.js';
import {
  trackRemoveFromWishlist,
  trackSnackbarUndoWishlistItemDelete
} from '../../../scripts/service/cube-tracking-layer.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {ADD_TO_WISHLIST_EVENT} from '../../../scripts/service/config.js';

@customElement('ents-wishlist-remove-button')
export class WishlistRemoveButtonElement extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: productMenuContext, subscribe: true})
  private productMenuState!: ProductMenuState;

  @consume({context: productsContext, subscribe: true})
  private productsState!: ProductsState;

  @property({type: Object, attribute: 'collection'})
  private collection?: WishlistCollection;

  @property({type: Object, attribute: 'product'})
  private product!: Product;

  static get styles(): CSSResult {
    return unsafeCSS(css);
  }

  private handleClick() {
    if (this.collection) {
      this.productMenuState.openMenu('remove-article', this.product, { collection: this.collection });
    } else {
      const productToRemove = this.product;
      this.removeFromWishlist(productToRemove);
      this.showSnackbarMessage(productToRemove);
    }
  }

  private removeFromWishlist(productToRemove: Product) {
    const data = this.getData(productToRemove);
    const trackingCallback = () => trackRemoveFromWishlist(
      data.syan,
      data.colorId,
      data.articleId,
      data.tracking,
      this.isOnAnyCollection(),
    );
    wishlistService.removeFromWishlist(data, trackingCallback);
  }

  private getData(productToRemove: Product) {
    return {
      identifier: ProductIdentifier.createByProduct(productToRemove).id,
      syan: productToRemove.syan,
      colorId: productToRemove.colorId,
      articleId: productToRemove.articleId,
      added: productToRemove.added,
      purchased: productToRemove.purchaseDetails?.purchased,
      collectionReferences: productToRemove.collectionReferences,
      tracking: {
        placement: 'wishlist',
        context: 'remove_icon',
        added: productToRemove.added,
      },
    };
  }

  private isOnAnyCollection(): boolean {
    return this.productsState.isProductInAnyCollection(
      ProductIdentifier.createByProduct(this.product)
    );
  }

  private showSnackbarMessage(productToRemove: Product) {
    const productElement = (this.getRootNode() as ShadowRoot).host as HTMLElement;

    // we use the images currentSrc so we are sure to get an image that is
    // in cache already to prevent snackbar layout shift
    const swiperElement = productElement.shadowRoot?.querySelector('ents-swiper')?.shadowRoot;
    const wishlistImage = swiperElement?.querySelector('ents-wishlist-image')?.shadowRoot;
    const pictureElement = (wishlistImage ?? swiperElement)?.querySelector('picture img') as HTMLImageElement;
    let articleDeletedText = this.messages['ents_wishlist_delete_title'];
    if (this.isOnAnyCollection()) {
      articleDeletedText = this.messages['ents_wishlist_delete_all_title'];
    }
    pushToSnackbar(
      articleDeletedText!,
      pictureElement.currentSrc,
      `${this.messages['ents_wishlist_delete_buttontext']}`,
      () => this.undoRemove(productToRemove)
    );
  }

  private undoRemove(productToRemove: Product) {
    trackSnackbarUndoWishlistItemDelete(productToRemove);
    document.dispatchEvent(new CustomEvent(ADD_TO_WISHLIST_EVENT, { detail:  this.getData(productToRemove) }));
  }

  protected render() {
    return html`
      <button @click="${this.handleClick}"
              type="button" aria-label="${this.messages['ents_wishlist_product_cta_remove']}">
        ${hideIconFromAssistiveTechnology(trashIcon())}
      </button>
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if(changedProperties.size === 0){
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }
}
